import React, { Component } from 'react';
import FaqSection from './FaqSection';
import './pentities.css';
import './Finance.css';
import NavBar from './NavBar';

interface FinanceProps {}

interface FinanceState {}

class Finance extends Component<FinanceProps, FinanceState> {
  render() {
    return (
      <div className="pentities-page finance-v2-page">
        {/* Top Navigation */}
        <NavBar />

        {/* Hero Section */}
        <section className="pentities-hero-section">
          <div className="pentities-hero-video-background">
            <video preload="metadata" autoPlay loop muted playsInline className="pentities-hero-video">
              <source src={require('./assets/video/web/15472087_1920_1080_24fps.mp4')} type="video/mp4" />
            </video>
          </div>
          <div className="pentities-container">
            <div className="pentities-hero-content">
              <span className="pentities-hero-eyebrow">The Finance Workspace</span>
              <h1 className="pentities-hero-title">
                Departmental finance budgets, invoices and payroll inside Microsoft Teams
              </h1>
              <p className="pentities-hero-description">
                One live view of budgets, spend, invoices, purchase orders, payroll, expenses and tax organised by
                department and fiscal year, with the source documents, emails and tasks attached to every number. It
                reads invoices with Azure Document Intelligence and runs entirely inside your own Microsoft 365 and
                Azure tenant, so no financial data ever leaves your control.
              </p>
              <div className="pentities-hero-cta">
                <a
                  href="https://outlook.office.com/book/META365Bookingforademo@e-study.gr/?ismsaljsauthenabled"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="pentities-hero-cta-btn"
                >
                  Request Demo
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* General Description / Executive summary */}
        <section id="overview" className="pentities-gendesc-section">
          <div className="pentities-container">
            <div className="pentities-gendesc-content">
              <span className="pentities-gendesc-label">Executive Summary</span>
              <h2 className="pentities-gendesc-title">One live picture of departmental spend with the evidence behind every number</h2>
              <p>
                Finance teams spend their days moving numbers between a budget spreadsheet, an accounting package, an
                inbox full of invoices, and whoever happens to know why a department is over-spent. The context is
                scattered, the alerts arrive late, and the documents behind every figure live somewhere else entirely.
              </p>
              <p>
                pEntities brings that together in one place. The Finance Workspace lives inside Microsoft Teams and
                gives finance and department managers a single, live view of budgets, spend, invoices, purchase orders,
                payroll, expenses and tax organised by department and fiscal year, with the source documents, emails
                and follow-up tasks attached to every record.
              </p>
              <p>
                Everything is live in the product today multi-year departmental budget tracking with traffic-light
                alerts, four live analytics visualisations, full invoice / purchase / payroll / expense / tax registers
                with document, email and Microsoft Planner task linkage, AI invoice data extraction, payroll with
                integrated performance visibility, and budget-threshold automation wired into the pEntities workflow
                engine.
              </p>
              <p>
                <b>Value for the organisation.</b> One live picture of departmental spend with the invoices, emails
                and tasks behind every number attached inside Microsoft Teams, on your own tenant, with no finance
                data leaving your control.
              </p>
            </div>
          </div>
        </section>

        {/* Part 1 The Finance Workspace (dark) */}
        <section id="finance-workspace" className="pentities-docmgmt-section">
          <div className="pentities-container">
            <span className="pentities-docmgmt-eyebrow">Part 1 The Finance Workspace</span>
            <h2 className="pentities-docmgmt-title">One live finance dashboard inside Teams budgets, analytics and every register</h2>
            <p className="pentities-docmgmt-lead">
              No separate finance portal, no export-and-email, no context-switching. The workspace is one live
              dashboard inside Microsoft Teams multi-year, fully localized, following the user's light or dark theme,
              on any screen.
            </p>

            <div className="pentities-docmgmt-block">
              <h3>1 · One workspace, right inside Teams</h3>
              <p>A single-page finance dashboard budgets, analytics and the departmental registers together.</p>
              <ul>
                <li><b>Everything on one page</b> selecting a department opens its expenses, invoices, purchases, payroll and tax in place.</li>
                <li><b>Any fiscal year</b> switch between years and every chart and figure recomputes instantly.</li>
                <li><b>In their language</b> fully localized, so finance and department managers across regions read it natively.</li>
                <li><b>Light or dark, any screen</b> follows the Teams theme and reflows down to the Teams mobile app.</li>
              </ul>
            </div>

            <div className="pentities-docmgmt-block">
              <h3>2 · Budgets under control</h3>
              <p>Budget control is the heart of the workspace, and it is always current.</p>
              <ul>
                <li><b>Per department, per year</b> every department carries allocated, spent and remaining budget for each fiscal year, in one grid.</li>
                <li><b>Traffic-light at a glance</b> spend and remaining figures colour green, amber or red as they approach their limits.</li>
                <li><b>Alerts that fire themselves</b> crossing 80%, 90% or 100% raises a budget-threshold event automatically, once per band, driving any downstream automation.</li>
              </ul>
            </div>

            <div className="pentities-docmgmt-block">
              <h3>3 · Live financial analytics</h3>
              <p>Four purpose-built visualisations turn the raw figures into an instant read on the organisation's finances.</p>
              <ul>
                <li><b>Budget overview</b> allocated, spent and remaining as clear progress bars, each with its percentage and health colour.</li>
                <li><b>Department budget donut</b> a share-of-budget donut with a per-department legend and totals.</li>
                <li><b>Expenses by department</b> a ranked bar chart of where the money is actually going.</li>
                <li><b>Payroll cost vs budget</b> a bullet chart pairing each department's payroll cost against its budget, with live headcount and a utilisation read.</li>
              </ul>
            </div>

            <div className="pentities-docmgmt-block">
              <h3>4 · The registers: invoices, purchases, payroll, expenses, tax</h3>
              <p>Under every department sit five registers, each a clean, editable record set.</p>
              <ul>
                <li><b>Invoices</b> number, date, vendor, amount and description, auto-numbered on creation.</li>
                <li><b>Purchase orders</b> number, title, date, supplier, amount and description.</li>
                <li><b>Payroll</b> employee, pay period, gross pay and deductions, with net pay calculated automatically.</li>
                <li><b>Expenses &amp; tax</b> departmental expense records, and tax data alongside taxable-income figures.</li>
                <li><b>One consistent experience</b> full create, edit and delete in the same panel, so a finance manager learns the workspace once.</li>
              </ul>
            </div>

            <div className="pentities-docmgmt-block">
              <h3>5 · Every record, in full context</h3>
              <p>A finance figure is only as good as the evidence behind it so every invoice, PO and record carries its own context.</p>
              <ul>
                <li><b>The documents</b> attach the actual invoice or receipt from SharePoint, view it in-app, and apply retention and compliance labels without leaving the record.</li>
                <li><b>The emails</b> attach the Outlook or Teams conversation behind a purchase, read it inline, or open it in Outlook.</li>
                <li><b>The follow-up</b> create and assign Microsoft Planner tasks against a record ("approve this payment", "chase this PO"), tracked to completion.</li>
                <li><b>A record summary</b> each record opens to a summary tile showing its total and how many documents, emails and tasks are attached.</li>
              </ul>
            </div>

            <div className="pentities-docmgmt-block">
              <h3>6 · AI that reads your invoices</h3>
              <p>The workspace does not just store invoices it can read them.</p>
              <ul>
                <li><b>Automatic extraction</b> an attached invoice is run through Azure Document Intelligence, which pulls out the invoice number, dates, PO reference, total, vendor and customer no manual typing.</li>
                <li><b>A catalogue of models</b> the same intelligence layer reads receipts, contracts and tax forms, so finance can extract structured data from more than just invoices.</li>
                <li><b>Usage tracked</b> every extraction is metered against the organisation's own AI service, so cost stays visible and controlled.</li>
              </ul>
            </div>

            <div className="pentities-docmgmt-block">
              <h3>7 · Payroll with performance built in</h3>
              <p>Payroll in the workspace is more than pay figures it is compensation in the context of performance.</p>
              <ul>
                <li><b>Net pay, calculated</b> enter gross pay and deductions and net pay is worked out automatically; the employee is picked directly from the directory.</li>
                <li><b>Compensation in context</b> each payroll record carries the employee's salary band, performance rating and last review date, and shows the latest HR review beside the pay.</li>
                <li><b>Kept in step with HR</b> saving a payroll record mirrors it back onto the employee's HR record, so finance and HR never drift apart.</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Part 2 The engine that runs it (white) */}
        <section id="engine" className="pentities-workflow-section">
          <div className="pentities-container">
            <span className="pentities-workflow-eyebrow">Part 2 The engine that runs it</span>
            <h2 className="pentities-workflow-title">Your data, in your tenant connected across the business</h2>
            <p className="pentities-workflow-lead">
              The Finance Workspace stores its data inside your own Microsoft 365, automates on budget events, and is
              wired into HR and the rest of the platform with nothing to migrate and no separate finance cloud.
            </p>

            <div className="pentities-workflow-block">
              <h3>8 · Your data, in your tenant</h3>
              <p>The Finance Workspace stores its data inside the organisation's own Microsoft 365, not a separate finance cloud.</p>
              <ul>
                <li><b>On your own drive</b> finance data is held with the business record it belongs to, in the organisation's own Microsoft 365 storage, reached through Microsoft Graph.</li>
                <li><b>Isolated by design</b> access is bound to the organisation's own drive and tenant there is no shared multi-tenant finance database.</li>
                <li><b>Derived, not duplicated</b> remaining budget, net pay, taxable income and receivable status are recalculated live from the source figures.</li>
              </ul>
            </div>

            <div className="pentities-workflow-block">
              <h3>9 · Automation on budget events</h3>
              <p>Budget moments can start automation on their own, through the pEntities workflow engine.</p>
              <ul>
                <li><b>Budget triggers</b> crossing 80%, 90% or 100% of a department budget fires a budget-threshold event carrying the department, fiscal year, threshold and figures.</li>
                <li><b>Do anything downstream</b> a triggered workflow can email finance, alert the department head, create an approval task or invoke AI assembled with no code.</li>
                <li><b>Reliable by construction</b> events run on the same durable execution engine that powers the rest of the platform, filtered to the departments and thresholds that matter.</li>
              </ul>
            </div>

            <div className="pentities-workflow-block">
              <h3>10 · Connected across the business</h3>
              <p>Finance does not sit alone it is wired into HR and the rest of the platform.</p>
              <ul>
                <li><b>Payroll and HR in sync</b> payroll records flow both ways between Finance and the HR module, so compensation and the employee record stay aligned.</li>
                <li><b>Leave that shows its cost</b> when leave is approved in HR, a costed financial risk can be raised against the department's budget automatically.</li>
                <li><b>Onboarding that budgets itself</b> a new hire can seed draft equipment and software expenses, so the cost of joining is captured from day one.</li>
              </ul>
            </div>

            <div className="pentities-workflow-block">
              <h3>11 · Security &amp; access</h3>
              <p>Who can reach finance data is governed by Microsoft 365 itself.</p>
              <ul>
                <li><b>Acts as the signed-in user</b> the workspace reaches finance data with delegated permissions, so it can never see data that person could not already see.</li>
                <li><b>Bound to the tenant</b> every request is scoped to the organisation's own Microsoft 365 tenant and drive.</li>
                <li><b>Governed where you already govern</b> access follows the same SharePoint and OneDrive permissions the organisation already manages.</li>
              </ul>
            </div>
          </div>
        </section>

        {/* How it compares (dark) */}
        <section id="compare" className="pentities-docmgmt-section finance-compare-section">
          <div className="pentities-container">
            <span className="pentities-docmgmt-eyebrow">How pEntities compares</span>
            <h2 className="pentities-docmgmt-title">A live layer on Microsoft 365 not a separate finance system</h2>
            <p className="pentities-docmgmt-lead">
              A traditional finance system is a separate destination with its own data, its own login and its own copy
              of your documents. The Finance Workspace is a live layer on the Microsoft 365 the organisation already
              runs.
            </p>
            <div className="finance-compare-table">
              <div className="finance-compare-row finance-compare-head">
                <span>Consideration</span>
                <span>pEntities Finance Workspace</span>
                <span>A separate finance system</span>
              </div>
              <div className="finance-compare-row">
                <span>Where you work</span>
                <span>Inside Microsoft Teams, beside the business record</span>
                <span>A separate application and login</span>
              </div>
              <div className="finance-compare-row">
                <span>The documents</span>
                <span>The real invoice, attached from SharePoint</span>
                <span>A re-uploaded copy in another silo</span>
              </div>
              <div className="finance-compare-row">
                <span>Invoice capture</span>
                <span>AI reading via Azure Document Intelligence</span>
                <span>Manual entry, or a paid add-on</span>
              </div>
              <div className="finance-compare-row">
                <span>Budget alerts</span>
                <span>Automatic at 80 / 90 / 100%</span>
                <span>Month-end reports, or manual checks</span>
              </div>
              <div className="finance-compare-row">
                <span>Payroll &amp; performance</span>
                <span>Pay and the HR review, together</span>
                <span>Separate systems, re-keyed</span>
              </div>
              <div className="finance-compare-row">
                <span>Where data lives</span>
                <span>Your own Microsoft 365 tenant</span>
                <span>A shared multi-tenant finance cloud</span>
              </div>
              <div className="finance-compare-row">
                <span>Automation</span>
                <span>No-code workflows on budget events</span>
                <span>Fixed features, or none</span>
              </div>
            </div>
          </div>
        </section>

        {/* Modules Section */}
        <section id="modules" className="pentities-modules-section">
          <div className="pentities-container">
            <span className="pentities-modules-eyebrow">pEntities Modules</span>
            <h2 className="pentities-modules-title">Purpose-built modules for the work your teams actually do</h2>
            <p className="pentities-modules-lead">
              One platform, four specialised modules each with its own domain AI agent, all running inside
              Microsoft Teams on the data you already own.
            </p>

            <div className="pentities-modules-grid">
              <a href="/solutions/hr-template" className="pentities-module-card">
                <h3>HR Module</h3>
                <p>
                  The full employee lifecycle onboarding with automatic task lists, leave with multi-level
                  approval, performance reviews, training tracked to its expiry date and payroll analysis. The HR
                  agent spots compliance risks and writes the reports for you.
                </p>
                <span className="pentities-module-link">Explore HR →</span>
              </a>

              <a href="/solutions/case-management" className="pentities-module-card">
                <h3>Legal Module</h3>
                <p>
                  Every case tracked from pre-trial to appeal court, lawyers, opponents, confidentiality level,
                  financial exposure and risk. Contracts carry milestones, auto-renewal detection and signature
                  history, while the agent analyses timelines and exposure scenarios.
                </p>
                <span className="pentities-module-link">Explore Legal →</span>
              </a>

              <a href="/solutions/ticketing" className="pentities-module-card">
                <h3>Ticketing Module</h3>
                <p>
                  Every request handled with precision IT, HR, finance or legal. Real-time tracking, automatic
                  notifications, approval flows and full history, with priorities, SLAs and automation rules that
                  work without anyone intervening.
                </p>
                <span className="pentities-module-link">Explore Ticketing →</span>
              </a>

              <a href="/solutions/finance" className="pentities-module-card">
                <h3>Finance Module</h3>
                <p>
                  Invoices, expenses, purchases, budgets, receivables and tax data organised by fiscal year. Every
                  figure links back to its original email, document and approval task full traceability, and an
                  agent that spots trends and deviations.
                </p>
                <span className="pentities-module-link">Explore Finance →</span>
              </a>
            </div>
          </div>
        </section>

        {/* Footer */}
        <FaqSection path="/solutions/finance" />
        <footer className="footer">
          <div className="container">
            <div className="footer-grid">
              <div className="footer-column">
                <h4>Products</h4>
                <ul>
                  <li><a href="/solutions/pentities">pEntities</a>
                    <ul className="footer-subnav">
                      <li><a href="/solutions/automation">Automation Module</a></li>
                      <li><a href="/solutions/ticketing">Ticketing Module</a></li>
                      <li><a href="/solutions/hr-template">HR Module</a></li>
                      <li><a href="/solutions/case-management">Legal Module</a></li>
                      <li><a href="/solutions/hr-hub">Employee Module</a></li>
                    </ul>
                  </li>
                  <li><a href="/solutions/pshareemail">PShareEmail</a></li>
                  <li><a href="/solutions/docsearch">DocSearch Tool</a></li>
                  <li><a href="/solutions/searchassistant">Search Assistant</a></li>
                </ul>
              </div>
              <div className="footer-column">
                <h4>Company</h4>
                <ul>
                  <li><a href="/about">About Us</a></li>
                  <li><a href="/get-started">Get Started</a></li>
                  <li><a href="/contactus">Contact Us</a></li>
                  <li><a href="/plans-pricing">Plans & Pricing</a></li>
                </ul>
              </div>
              <div className="footer-column">
                <h4>Support</h4>
                <ul>
                  <li><a href="/knowledge">Knowledge Base</a></li>
                  <li><a href="/technical-support">Technical Support</a></li>
                </ul>
              </div>
              <div className="footer-column">
                <h4>Contact Us</h4>
                <div className="footer-office">
                  <p className="office-name">Athens Office</p>
                  <p>365 Kifisias Avenue, Kifisia, 14561</p>
                  <p>Ground Floor, Inside the Arcade</p>
                  <p>Tel: (+30)-210-260-3764</p>
                </div>
                <div className="footer-office">
                  <p className="office-name">Thessaloniki Office</p>
                  <p>15 Nikiforou Ouranou & Minotavrou Streets</p>
                  <p>Thessaloniki, 54627, PORTO CENTER, Building G1, 3rd Floor</p>
                  <p>Tel: (+30)-231-005-3361</p>
                </div>
                <div className="footer-office">
                  <p className="office-name">Crete Office</p>
                  <p>Kisamos, Chania</p>
                  <p>Tel: (+30)-2822-051309</p>
                </div>
                <p className="footer-email">info@meta-365.com</p>
              </div>
            </div>
            <div className="footer-bottom">
              <p>&copy; 2026 Meta-365. All rights reserved.</p>
              <div className="footer-social">
                <a href="https://www.linkedin.com/company/meta-365" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn"><img loading="lazy" decoding="async" src="data:image/svg+xml,%3Csvg%20xmlns='http://www.w3.org/2000/svg'%20viewBox='0%200%2024%2024'%20fill='white'%3E%3Cpath%20d='M4.98%203.5C4.98%204.88%203.87%206%202.5%206S0%204.88%200%203.5%201.12%201%202.5%201s2.48%201.12%202.48%202.5zM.25%208h4.5v13H.25V8zM8.5%208h4.31v1.78h.06c.6-1.14%202.07-2.34%204.26-2.34C21.5%207.44%2022.5%2010%2022.5%2014.02V21H18v-6.1c0-1.46-.03-3.33-2.03-3.33-2.03%200-2.34%201.58-2.34%203.22V21H8.5V8z'/%3E%3C/svg%3E" alt="LinkedIn" width="18" height="18" /></a>
                <a href="https://www.facebook.com/profile.php?id=61589941284929" target="_blank" rel="noopener noreferrer" aria-label="Facebook"><img loading="lazy" decoding="async" src="data:image/svg+xml,%3Csvg%20xmlns='http://www.w3.org/2000/svg'%20viewBox='0%200%2024%2024'%20fill='white'%3E%3Cpath%20d='M24%2012.07C24%205.4%2018.63%200%2012%200S0%205.4%200%2012.07C0%2018.1%204.39%2023.1%2010.13%2024v-8.44H7.08v-3.49h3.05V9.41c0-3.02%201.79-4.69%204.53-4.69%201.31%200%202.68.24%202.68.24v2.97h-1.51c-1.49%200-1.96.93-1.96%201.89v2.25h3.33l-.53%203.49h-2.8V24C19.61%2023.1%2024%2018.1%2024%2012.07z'/%3E%3C/svg%3E" alt="Facebook" width="18" height="18" /></a>
              </div>
              <div className="footer-links">
                <a href="/privacy-policy">Privacy Policy</a>
                <a href="/terms-of-use">Terms & Conditions</a>
              </div>
            </div>
          </div>
        </footer>
      </div>
    );
  }
}

export default Finance;
